import { React, useEffect, useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import * as bootstrap from 'bootstrap';
import { supportServices } from '../services/SupportServices';

const Support = () => {
    const [enquiriesData, setEnquiryData] = useState([]);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [showModal, setShowModal] = useState(false);
    const modalRef = useRef(null);
    
    const userId = localStorage.getItem('id');

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const fetchEnquiryData = async (page = 1, size = perPage) => {
        setLoading(true);
        try {
            // Assuming your API supports pagination parameters
            const response = await supportServices.fetchSupportEnquires(userId, { page, limit: size });
            if (response.status === 200) {
                const data = response.data;
                setEnquiryData(data.enquiries || data); // Adjust based on your API response structure
                setTotalRows(data.total || data.length); // Set total count for pagination
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) {
            fetchEnquiryData();
        }
    }, [userId]);

    const columns = [
        {
            name: 'Ticket ID',
            selector: row => row.ticket_id,
            sortable: true,
            width: '120px',
        },
        {
            name: 'Subject',
            selector: row => row.subject,
            sortable: true,
            grow: 2,
        },
        {
            name: 'Status',
            selector: row => row.status,
            cell: row => (
                row.status === 'pending' ? (
                    <span className="badge bg-warning">Pending</span>
                ) : row.status === 'in_progress' ? (
                    <span className="badge bg-info">In Progress</span>
                ) : row.status === 'on_hold' ? (
                    <span className="badge bg-danger">On Hold</span>
                ) : row.status === 'completed' ? (
                    <span className="badge bg-success">Completed</span>
                ) : (
                    <span className="badge bg-secondary">Pending</span>
                )
            ),
            sortable: true,
            width: '130px',
        },
        {
            name: 'Last Update',
            selector: row => formatDate(row.updatedAt),
            sortable: true,
            width: '130px',
        }
    ];

    const handlePageChange = (page) => {
        fetchEnquiryData(page, perPage);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        fetchEnquiryData(page, newPerPage);
    };

    const resetForm = () => {
        setSubject('');
        setMessage('');
    };

    const openModal = () => {
        resetForm();
        setShowModal(true);
        // Use Bootstrap modal programmatically
        setTimeout(() => {
            const modalElement = modalRef.current;
            if (modalElement) {
                const modal = new bootstrap.Modal(modalElement, {
                    backdrop: 'static',
                    keyboard: false
                });
                modal.show();
            }
        }, 100);
    };

    const closeModal = () => {
        const modalElement = modalRef.current;
        if (modalElement) {
            const modalInstance = bootstrap.Modal.getInstance(modalElement);
            if (modalInstance) {
                modalInstance.hide();
            }
        }
        setShowModal(false);
        
        // Clean up any remaining backdrops
        setTimeout(() => {
            const backdrops = document.querySelectorAll('.modal-backdrop');
            backdrops.forEach(backdrop => backdrop.remove());
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
        }, 300);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!subject.trim() || !message.trim()) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            const data = {
                user_id: userId,
                subject: subject.trim(),
                message: message.trim(),
            };

            const response = await supportServices.addSupportRequest(data);
            
            if (response.status === 201) {
                toast.success('We received your enquiry. We will contact you soon!');
                
                // Close modal and reset form
                closeModal();
                
                // Refresh data
                fetchEnquiryData();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'There are some errors in the fields. Please check again.';
            toast.error(errorMessage);
        }
    };

    const handleModalShow = () => {
        resetForm();
    };

    // Clean up on component unmount
    useEffect(() => {
        return () => {
            // Clean up any remaining backdrops on unmount
            const backdrops = document.querySelectorAll('.modal-backdrop');
            backdrops.forEach(backdrop => backdrop.remove());
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
        };
    }, []);

    const customStyles = {
        rows: {
            style: {
                minHeight: '56px',
            },
        },
        headCells: {
            style: {
                paddingLeft: '8px',
                paddingRight: '8px',
                fontWeight: 'bold',
                backgroundColor: '#f8f9fa',
            },
        },
        cells: {
            style: {
                paddingLeft: '8px',
                paddingRight: '8px',
            },
        },
    };

    return (
        <>
            <div className="body d-flex py-3">
                <div className="container-xxl">
                    <div className="row align-items-center">
                        <div className="border-0 mb-4">
                            <div className="card-header py-3 no-bg bg-transparent d-flex align-items-center px-0 justify-content-between border-bottom flex-wrap">
                                <h3 className="fw-bold mb-0">Support</h3>
                                <button 
                                    className="btn btn-primary step-btn" 
                                    onClick={openModal}
                                    type="button"
                                >
                                    + Add Ticket
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="row align-item-center">
                        <div className="col-md-12">
                            <div className="card mb-3">
                                <div className="card-header py-3 bg-transparent border-bottom-0">
                                    <h6 className="mb-0 fw-bold">Support Enquiries</h6>
                                </div>
                                <div className="card-body">
                                    <DataTable
                                        columns={columns}
                                        data={enquiriesData}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        paginationDefaultPage={1}
                                        paginationPerPage={perPage}
                                        paginationRowsPerPageOptions={[5, 10, 20, 50]}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onChangePage={handlePageChange}
                                        progressPending={loading}
                                        highlightOnHover
                                        striped
                                        responsive
                                        customStyles={customStyles}
                                        noDataComponent={
                                            <div className="text-center py-4">
                                                <p className="mb-0">No support tickets found</p>
                                            </div>
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="addEnquiry" tabIndex="-1" aria-hidden="true" ref={modalRef}>
                <div className="modal-dialog modal-dialog-centered modal-md modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold" id="expeditLabel1111">New Support Ticket</h5>
                            <button type="button" className="btn-close" onClick={closeModal} aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="deadline-form">
                                <form onSubmit={handleSubmit}>
                                    <div className="row g-3 mb-3">
                                        <div className="col-sm-12">
                                            <label htmlFor="subject" className="form-label">
                                                Subject <span className="text-danger">*</span>
                                            </label>
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                id="subject"
                                                value={subject}
                                                onChange={(e) => setSubject(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className="col-sm-12">
                                            <label htmlFor="message" className="form-label">
                                                Message <span className="text-danger">*</span>
                                            </label>
                                            <textarea 
                                                className="form-control" 
                                                id="message" 
                                                rows="4" 
                                                spellCheck="false"
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                Cancel
                            </button>
                            <button 
                                onClick={handleSubmit} 
                                type="button" 
                                className="btn btn-primary"
                                disabled={!subject.trim() || !message.trim()}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Support;